<template>
    <section class="addon-section">
        <header class="section-header">
            <h1 class="section-title">Dodatki</h1>
            <button class="btn btn-submit" @click="openForm()">
                <font-awesome-icon icon="plus" /> Dodaj
            </button>
        </header>

        <EntityFilterBar v-model:search="searchQuery" v-model:sort="sortModel" v-model:filters="filters"
            :filters-schema="[{ key: 'group', label: 'Grupa', type: 'select', options: groupOptions }]"
            :sort-options="[{ key: 'code', label: 'Kod' }, { key: 'group', label: 'Grupa' }]" />

        <PaginationControls :current-page="currentPage" :total-pages="totalPages" @update:page="goToPage" />

        <div class="status-wrapper">
            <p v-if="addonStore.loading" class="status-loading">Ładowanie...</p>
        </div>

        <div v-if="!addonStore.loading">
            <div v-if="filteredAddons.length" class="addon-list">
                <EntityCard v-for="addon in paginatedAddons" :key="addon.id">
                    <template #content>
                        <div class="addon-content">
                            <img v-if="addon.image_url" :src="addon.image_url" alt="Zdjęcie dodatku"
                                class="addon-image" />
                            <div class="addon-details">
                                <div class="detail-item">
                                    <span class="detail-label">Kod:</span>
                                    <span>{{ addonStore.formatCode(addon.code) }}</span>
                                </div>
                                <div class="detail-item">
                                    <span class="detail-label">Opis:</span>
                                    <span>{{ addon.desc }}</span>
                                </div>
                                <div class="detail-item">
                                    <span class="detail-label">Grupa:</span>
                                    <span>{{ addon.group }}</span>
                                </div>
                            </div>
                        </div>
                    </template>

                    <template #actions>
                        <button class="btn btn-edit" @click="editAddon(addon)">
                            <font-awesome-icon icon="pen" />
                            Edytuj
                        </button>
                        <button class="btn btn-delete" @click="confirmDelete(addon.id)">
                            <font-awesome-icon icon="trash" />
                            Usuń
                        </button>
                    </template>
                </EntityCard>
            </div>

            <div v-else class="empty-state">
                <font-awesome-icon icon="puzzle-piece" size="2x" />
                <p>Brak dodatków do wyświetlenia</p>
            </div>
        </div>

        <PaginationControls :current-page="currentPage" :total-pages="totalPages" @update:page="goToPage" />

        <teleport to="body">
            <transition name="fade">
                <AddonForm v-if="showForm" :visible="showForm" :addon="editingAddon" @close="showForm = false"
                    @saved="handleSave" />
            </transition>
        </teleport>

        <teleport to="body">
            <ConfirmDialog :visible="confirmDialog.state.isVisible" :title="confirmDialog.state.dialogOptions.title"
                :message="confirmDialog.state.dialogOptions.message"
                :confirmText="confirmDialog.state.dialogOptions.confirmText"
                :cancelText="confirmDialog.state.dialogOptions.cancelText"
                :danger="confirmDialog.state.dialogOptions.danger" @confirm="confirmDialog.handleConfirm"
                @cancel="confirmDialog.handleCancel" @close="confirmDialog.handleClose" />
        </teleport>
    </section>
</template>

<script>
import { useAddonStore } from '@/stores/addonStore'
import { showErrorToast, showSuccessToast } from '@/utils/errorUtils'
import { useConfirmDialog } from '@/composables/useConfirmDialog'
import EntityCard from '@/components/cms/EntityCard.vue'
import EntityFilterBar from '@/components/cms/EntityFilterBar.vue'
import AddonForm from './AddonForm.vue'
import PaginationControls from '@/components/cms/PaginationControls.vue'
import ConfirmDialog from '@/components/cms/ConfirmDialog.vue'

export default {
    components: { EntityCard, EntityFilterBar, AddonForm, PaginationControls, ConfirmDialog },
    setup() {
        const confirmDialog = useConfirmDialog()
        return { confirmDialog }
    },
    data() {
        return {
            addonStore: useAddonStore(),
            searchQuery: '',
            sortModel: {
                by: '',
                order: 'asc'
            },
            filters: {
                group: ''
            },
            showForm: false,
            editingAddon: null,
            currentPage: 1,
            itemsPerPage: 50,
        }
    },
    computed: {
        filteredAddons() {
            let filtered = this.addonStore.addons

            if (this.searchQuery) {
                filtered = filtered.filter(addon =>
                    addon.code?.toLowerCase().includes(this.searchQuery.toLowerCase()) ||
                    addon.desc?.toLowerCase().includes(this.searchQuery.toLowerCase())
                )
            }

            if (this.filters.group) {
                filtered = filtered.filter(
                    addon => addon.group === this.filters.group
                )
            }

            if (this.sortModel.by) {
                filtered = [...filtered].sort((a, b) => {
                    const fieldA = a[this.sortModel.by]
                    const fieldB = b[this.sortModel.by]
                    if (fieldA < fieldB) return this.sortModel.order === 'asc' ? -1 : 1
                    if (fieldA > fieldB) return this.sortModel.order === 'asc' ? 1 : -1
                    return 0
                })
            }

            return filtered
        },

        paginatedAddons() {
            const start = (this.currentPage - 1) * this.itemsPerPage
            return this.filteredAddons.slice(start, start + this.itemsPerPage)
        },

        totalPages() {
            return Math.ceil(this.filteredAddons.length / this.itemsPerPage)
        },

        groupOptions() {
            const uniqueGroups = [...new Set(this.addonStore.addons.map(a => a.group).filter(Boolean))]
            return uniqueGroups.map(name => ({ id: name, name }))
        }
    },
    methods: {
        openForm(addon = null) {
            this.editingAddon = addon
            this.showForm = true
        },
        editAddon(addon) {
            this.openForm(addon)
        },
        async confirmDelete(id) {
            const confirmed = await this.confirmDialog.showConfirm({
                title: 'Usunąć dodatek?',
                message: 'Ta operacja jest nieodwracalna. Wszystkie dane związane z tym dodatkiem zostaną usunięte.',
                confirmText: 'Tak, usuń',
                cancelText: 'Anuluj',
                danger: true
            })

            if (confirmed) {
                try {
                    await this.addonStore.deleteAddon(id)
                    showSuccessToast('Usunięto dodatek.')
                } catch (err) {
                    showErrorToast(err, 'Błąd przy usuwaniu dodatku.')
                }
            }
        },
        handleSave() {
            this.addonStore.fetchAddons()
            this.showForm = false
        },
        goToPage(page) {
            if (page >= 1 && page <= this.totalPages) {
                this.currentPage = page
            }
        },
    },
    created() {
        if (this.addonStore.addons.length === 0) {
            this.addonStore.fetchAddons()
        }
    }
}
</script>


<style scoped lang="scss">
.addon-section {
    max-width: 1200px;
    margin: 0 auto;
    cursor: default;
}

.section-header {
    margin-bottom: 1rem;
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 1rem;

    @media (max-width: 640px) {
        align-items: flex-start;
        flex-direction: column;
    }
}

.section-title {
    font-size: 1.8rem;
    font-weight: 700;
    color: $color-primary-dark;
    margin: 0;
}

.status-wrapper {
    margin-bottom: 1.5rem;
}

.status-loading {
    font-size: 1rem;
    color: $color-text-light;
    text-align: center;
}

.addon-list {
    display: grid;
    gap: 1.25rem;
}

.addon-content {
    display: flex;
    flex-direction: column;
    gap: 1.5rem;

    @media (min-width: 768px) {
        flex-direction: row;
        align-items: flex-start;
    }
}

.addon-image {
    width: 200px;
    height: 150px;
    object-fit: contain;
    border-radius: 6px;
    border: 1px solid $color-grey;
    flex-shrink: 0;
    background-color: $color-background;
}

.addon-details {
    display: grid;
    gap: 0.5rem;
    flex: 1;
}

.detail-item {
    display: flex;
    align-items: flex-start;
    gap: 0.5rem;
    font-size: 0.95rem;
}

.detail-label {
    font-weight: 600;
    color: $color-text;
    min-width: 100px;
}

.btn {
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.5rem 1rem;
    border: none;
    border-radius: 6px;
    font-size: 0.9rem;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s ease;

    svg {
        flex-shrink: 0;
    }

    &-edit {
        background: $color-primary;
        color: white;

        &:hover {
            background: $color-primary-dark;
        }
    }

    &-delete {
        background: $color-grey-light;
        color: $color-primary-accent;

        &:hover {
            background: $color-primary-accent;
            color: white;
        }
    }

    &-submit {
        background: $color-primary;
        color: white;

        &:hover {
            background: $color-primary-dark;
        }

        &:disabled {
            opacity: 0.6;
            cursor: not-allowed;
        }
    }
}

.empty-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 1rem;
    padding: 3rem 2rem;
    text-align: center;
    background: $color-white;
    border-radius: 8px;
    border: 1px solid $color-grey;

    svg {
        color: $color-grey-dark;
    }

    p {
        color: $color-text-light;
        font-size: 1.1rem;
        margin: 0;
    }
}

.fade-enter-active,
.fade-leave-active {
    transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
    opacity: 0;
}
</style>
